import { useState, useEffect } from "react";
import "./Common.css";
import { useNavigate } from "react-router-dom";
import { createPlayer, uploadPlayerPhoto, uploadAgeProof, registerMembership } from "../API/Api.js";

/* ── Custom SVGs for internal icons ── */
const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
);
const MapPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
);
const FileTextIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 22 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
);
const UploadCloudIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" /><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" /><polyline points="16 16 12 12 8 16" /></svg>
);
const AlertCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
);
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
);
const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
);
const ShuttleBadgeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13" cy="13" r="11" />
    <circle cx="13" cy="13" r="5" />
    <line x1="13" y1="2" x2="13" y2="24" />
    <line x1="2" y1="13" x2="24" y2="13" />
  </svg>
);

const PlayerRegistration = () => {
  const navigate = useNavigate();
  const GUJARAT_DISTRICTS = [
    "Anand", "Ahmedabad", "Vadodara", "Surat", "Rajkot", "Amreli",
    "Banaskantha", "Bharuch", "Bhavnagar", "Dahod", "Gandhinagar",
    "Jamnagar", "Junagadh", "Kutch", "Mehsana", "Morbi", "Narmada",
    "Navsari", "Panchmahal", "Patan", "Porbandar", "Sabarkantha",
    "Tapi", "Valsad", "Other"
  ];

  const ANAND_TALUKAS = [
    "Anand", "Anklav", "Borsad", "Khambhat",
    "Petlad", "Sojitra", "Tarapur", "Umreth"
  ];

  // Form data (PascalCase for internal use)
  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    Dob: "",
    Gender: "",
    District: "Anand",
    SubDistrict: "",
    Village: "",
    InstitutionName: "",
    Address: "",
    PhotoUrl: "",   // base64 preview
    AgeProofUrl: "", // base64 preview
  });

  const [files, setFiles] = useState({
    photoFile: null,
    ageProofFile: null
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [subDistrictSearch, setSubDistrictSearch] = useState("");
  const [isOpenSubDistrict, setIsOpenSubDistrict] = useState(false);

  // Close custom dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpenSubDistrict && !event.target.closest(".custom-dropdown-container")) {
        setIsOpenSubDistrict(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpenSubDistrict]);

  // Reset SubDistrict when District changes
  useEffect(() => {
    if (formData.District === "Anand") {
      setFormData(prev => ({ ...prev, SubDistrict: ANAND_TALUKAS[0] }));
    } else {
      setFormData(prev => ({ ...prev, SubDistrict: "" }));
    }
  }, [formData.District]);

  // Step validations
  const validateStep = (step) => {
    let stepErrors = {};
    if (step === 1) {
      if (!formData.FirstName.trim()) stepErrors.FirstName = "First Name is required";
      if (!formData.LastName.trim()) stepErrors.LastName = "Last Name is required";
      if (!formData.Dob) stepErrors.Dob = "Date of Birth is required";
      if (!formData.Gender) stepErrors.Gender = "Please select your gender";
    } else if (step === 2) {
      if (!formData.District) stepErrors.District = "District is required";
      if (!formData.SubDistrict.trim()) stepErrors.SubDistrict = "Sub-district/Taluka is required";
      if (!formData.Village.trim()) stepErrors.Village = "Village/Town is required";
      if (!formData.Address.trim()) stepErrors.Address = "Address is required";
    } else if (step === 3) {
      if (!formData.PhotoUrl) stepErrors.PhotoUrl = "Profile photo is required for player verification";
      if (!formData.AgeProofUrl) stepErrors.AgeProofUrl = "Age proof document is required (Birth Cert / Aadhaar)";
    }
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 200, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleGenderSelect = (genderValue) => {
    setFormData(prev => ({ ...prev, Gender: genderValue }));
    if (errors.Gender) setErrors(prev => ({ ...prev, Gender: null }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [fieldName]: "File size exceeds 5MB limit" }));
      return;
    }
    setFiles(prev => ({
      ...prev,
      [fieldName === "PhotoUrl" ? "photoFile" : "ageProofFile"]: file
    }));
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, [fieldName]: reader.result }));
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    };
    reader.readAsDataURL(file);
  };

  const removeFile = (fieldName) => {
    setFiles(prev => ({
      ...prev,
      [fieldName === "PhotoUrl" ? "photoFile" : "ageProofFile"]: null
    }));
    setFormData(prev => ({ ...prev, [fieldName]: "" }));
  };

  // ------------------------------------------------------------
  // API SUBMISSION (using real endpoints)
  // ------------------------------------------------------------
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;
    setIsSubmitting(true);
    setErrors({});

    try {
      // 1. Create player
      const createPayload = {
        firstName: formData.FirstName,
        lastName: formData.LastName,
        dob: new Date(formData.Dob).toISOString(),
        gender: formData.Gender,
        district: formData.District,
        subDistrict: formData.SubDistrict,
        village: formData.Village,
        institutionName: formData.InstitutionName || "",
        address: formData.Address,
      };
      const createResponse = await createPlayer(createPayload);
      const player = createResponse.data;
      const playerId = player.playerId || player.id;
      if (!playerId) throw new Error("Player creation did not return a valid ID");

      // 2. Upload profile photo (if any)
      if (files.photoFile) {
        await uploadPlayerPhoto(playerId, files.photoFile);
      }

      // 3. Upload age proof document (if any)
      if (files.ageProofFile) {
        await uploadAgeProof(playerId, files.ageProofFile);
      }
      try {
        await registerMembership(playerId, 1);
        console.log("Membership registered successfully for player:", playerId);
      } catch (membershipErr) {
        console.error("Membership registration failed:", membershipErr);
        // Optional: show a non-blocking warning to the user
        // We still consider the overall registration successful.
      }

      // Build final player object
      const finalPlayer = {
        ...player,
        PhotoUrl: formData.PhotoUrl,   // keep base64 preview for display
        AgeProofUrl: formData.AgeProofUrl,
        PlayerCode: player.playerCode || `ADBA-PL-${Math.floor(Math.random() * 9000) + 1000}`,
      };

      // Save to sessionStorage and redirect immediately
      sessionStorage.setItem("registered_player", JSON.stringify(finalPlayer));
      navigate("/userdashboard", { state: { player: finalPlayer } });

    } catch (err) {
      console.error("Registration failed:", err);
      let errorMsg = "Unable to complete registration. Please check your network and try again.";
      if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        errorMsg = Object.values(apiErrors).flat().join(" ");
      } else if (err.response?.data?.title) {
        errorMsg = err.response.data.title;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setErrors({ api: errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };



  // ------------------------------------------------------------
  // STEP RENDERING (identical to original – omitted for brevity)
  // ------------------------------------------------------------
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="reg-grid-2">
            <div className="reg-step-header reg-field full">
              <div className="reg-step-header-tag"><div className="reg-step-header-tag-bar" />Step 1 of 3</div>
              <h2 className="reg-step-title">Personal Profile</h2>
            </div>
            <div className="reg-field">
              <label className="reg-label" htmlFor="FirstName">First Name <span className="req">*</span></label>
              <div className="reg-input-wrapper">
                <input type="text" id="FirstName" name="FirstName" value={formData.FirstName} onChange={handleInputChange} className="reg-input" placeholder="e.g. Raj" />
              </div>
              {errors.FirstName && <div className="reg-error-msg"><AlertCircleIcon /> {errors.FirstName}</div>}
            </div>
            <div className="reg-field">
              <label className="reg-label" htmlFor="LastName">Last Name <span className="req">*</span></label>
              <div className="reg-input-wrapper">
                <input type="text" id="LastName" name="LastName" value={formData.LastName} onChange={handleInputChange} className="reg-input" placeholder="e.g. Patel" />
              </div>
              {errors.LastName && <div className="reg-error-msg"><AlertCircleIcon /> {errors.LastName}</div>}
            </div>
            <div className="reg-field">
              <label className="reg-label" htmlFor="Dob">Date of Birth <span className="req">*</span></label>
              <div className="reg-input-wrapper">
                <input type="date" id="Dob" name="Dob" value={formData.Dob} onChange={handleInputChange} className="reg-input" max={new Date().toISOString().split("T")[0]} />
              </div>
              {errors.Dob && <div className="reg-error-msg"><AlertCircleIcon /> {errors.Dob}</div>}
            </div>
            <div className="reg-field">
              <label className="reg-label">Gender <span className="req">*</span></label>
              <div className="reg-gender-cards">
                {["Male", "Female", "Other"].map(g => (
                  <div key={g} className={`reg-gender-card ${formData.Gender === g ? "selected" : ""}`} onClick={() => handleGenderSelect(g)}>
                    <span className="reg-gender-icon">{g === "Male" ? "👨" : g === "Female" ? "👩" : "👤"}</span>
                    <span className="reg-gender-label">{g}</span>
                  </div>
                ))}
              </div>
              {errors.Gender && <div className="reg-error-msg"><AlertCircleIcon /> {errors.Gender}</div>}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="reg-grid-2">
            <div className="reg-step-header reg-field full">
              <div className="reg-step-header-tag"><div className="reg-step-header-tag-bar" />Step 2 of 3</div>
              <h2 className="reg-step-title">Location & Institution</h2>
            </div>
            <div className="reg-field full">
              <label className="reg-label" htmlFor="InstitutionName">Institution / School / Club Name <span style={{ opacity: 0.5 }}>(Optional)</span></label>
              <div className="reg-input-wrapper">
                <input type="text" id="InstitutionName" name="InstitutionName" value={formData.InstitutionName} onChange={handleInputChange} className="reg-input" placeholder="e.g. V.P. Science College" />
              </div>
            </div>
            <div className="reg-field">
              <label className="reg-label" htmlFor="District">District <span className="req">*</span></label>
              <div className="reg-input-wrapper">
                <select id="District" name="District" value={formData.District} onChange={handleInputChange} className="reg-input reg-select" disabled style={{ cursor: "not-allowed", opacity: 0.85, background: "var(--off)" }}>
                  {GUJARAT_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <svg className="reg-select-arrow" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
            </div>
            <div className="reg-field">
              <label className="reg-label" htmlFor="SubDistrict">Sub-District / Taluka <span className="req">*</span></label>
              <div className="reg-input-wrapper custom-dropdown-container">
                {formData.District === "Anand" ? (
                  <>
                    <div className={`reg-input custom-dropdown-trigger ${isOpenSubDistrict ? "active" : ""}`} onClick={() => setIsOpenSubDistrict(!isOpenSubDistrict)} style={{ cursor: "pointer", paddingRight: "40px" }}>
                      <span>{formData.SubDistrict || "Select Sub-District"}</span>
                    </div>
                    <svg className="reg-select-arrow" viewBox="0 0 24 24" style={{ transform: isOpenSubDistrict ? "rotate(180deg)" : "none", transition: "transform 0.28s" }}><polyline points="6 9 12 15 18 9" /></svg>
                    {isOpenSubDistrict && (
                      <div className="custom-dropdown-menu" style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1.5px solid var(--border)", borderRadius: "10px", boxShadow: "0 12px 36px rgba(0,0,0,0.06)", zIndex: 100, maxHeight: "260px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <div style={{ padding: "8px", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                          <input type="text" placeholder="Search Taluka..." value={subDistrictSearch} onChange={(e) => setSubDistrictSearch(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1.5px solid var(--border)", outline: "none" }} autoFocus />
                        </div>
                        <div style={{ overflowY: "auto", maxHeight: "200px" }}>
                          {ANAND_TALUKAS.filter(t => t.toLowerCase().includes(subDistrictSearch.toLowerCase())).map(t => (
                            <div key={t} onClick={() => { setFormData({ ...formData, SubDistrict: t }); setIsOpenSubDistrict(false); setSubDistrictSearch(""); }} style={{ padding: "12px 18px", cursor: "pointer", fontWeight: formData.SubDistrict === t ? "700" : "500", background: formData.SubDistrict === t ? "rgba(204,0,0,0.05)" : "transparent", color: formData.SubDistrict === t ? "var(--r3)" : "var(--text)" }}>{t}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <input type="text" id="SubDistrict" name="SubDistrict" value={formData.SubDistrict} onChange={handleInputChange} className="reg-input" placeholder="e.g. Daskroi" />
                )}
              </div>
              {errors.SubDistrict && <div className="reg-error-msg"><AlertCircleIcon /> {errors.SubDistrict}</div>}
            </div>
            <div className="reg-field full">
              <label className="reg-label" htmlFor="Village">Village / Town / City <span className="req">*</span></label>
              <div className="reg-input-wrapper">
                <input type="text" id="Village" name="Village" value={formData.Village} onChange={handleInputChange} className="reg-input" placeholder="e.g. Vallabh Vidyanagar" />
              </div>
              {errors.Village && <div className="reg-error-msg"><AlertCircleIcon /> {errors.Village}</div>}
            </div>
            <div className="reg-field full">
              <label className="reg-label" htmlFor="Address">Residential Address <span className="req">*</span></label>
              <div className="reg-input-wrapper">
                <textarea id="Address" name="Address" value={formData.Address} onChange={handleInputChange} className="reg-input reg-textarea" placeholder="Street Name, House No, Landmark, Area Pincode" />
              </div>
              {errors.Address && <div className="reg-error-msg"><AlertCircleIcon /> {errors.Address}</div>}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="reg-grid-2">
            <div className="reg-step-header reg-field full">
              <div className="reg-step-header-tag"><div className="reg-step-header-tag-bar" />Step 3 of 3</div>
              <h2 className="reg-step-title">Document Verification</h2>
            </div>
            <div className="reg-field">
              <label className="reg-label">Passport Profile Photo <span className="req">*</span></label>
              {!formData.PhotoUrl ? (
                <div className="reg-dropzone">
                  <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, "PhotoUrl")} />
                  <div className="reg-dropzone-icon"><UploadCloudIcon /></div>
                  <span className="reg-dropzone-text">Choose / Drag Profile Image</span>
                  <span className="reg-dropzone-subtext">JPG or PNG formats (Max 5MB)</span>
                </div>
              ) : (
                <div className="reg-photo-preview-container">
                  <div className="reg-photo-avatar"><img src={formData.PhotoUrl} alt="Preview" /></div>
                  <div className="reg-photo-info"><span className="reg-photo-info-name">{files.photoFile?.name || "PlayerPhoto.jpg"}</span><span className="reg-photo-info-size">{files.photoFile ? (files.photoFile.size / 1048576).toFixed(2) + " MB" : "Uploaded"}</span></div>
                  <button type="button" className="reg-photo-remove" onClick={() => removeFile("PhotoUrl")}>✕</button>
                </div>
              )}
              {errors.PhotoUrl && <div className="reg-error-msg"><AlertCircleIcon /> {errors.PhotoUrl}</div>}
            </div>
            <div className="reg-field">
              <label className="reg-label">Age Proof Document <span className="req">*</span></label>
              {!formData.AgeProofUrl ? (
                <div className="reg-dropzone">
                  <input type="file" accept=".pdf,image/*" onChange={(e) => handleFileChange(e, "AgeProofUrl")} />
                  <div className="reg-dropzone-icon"><FileTextIcon /></div>
                  <span className="reg-dropzone-text">Choose / Drag Age Proof</span>
                  <span className="reg-dropzone-subtext">Birth Certificate / Aadhaar (Max 5MB)</span>
                </div>
              ) : (
                <div className="reg-doc-preview-container">
                  <div className="reg-doc-icon"><FileTextIcon /></div>
                  <div className="reg-photo-info"><span className="reg-photo-info-name">{files.ageProofFile?.name || "AgeProofDocument.pdf"}</span><span className="reg-photo-info-size">{files.ageProofFile ? (files.ageProofFile.size / 1048576).toFixed(2) + " MB" : "Uploaded"}</span></div>
                  <button type="button" className="reg-photo-remove" onClick={() => removeFile("AgeProofUrl")}>✕</button>
                </div>
              )}
              {errors.AgeProofUrl && <div className="reg-error-msg"><AlertCircleIcon /> {errors.AgeProofUrl}</div>}
            </div>
            {errors.api && <div className="reg-field full"><div className="reg-error-msg"><AlertCircleIcon /> {errors.api}</div></div>}
          </div>
        );
      default: return null;
    }
  };

  return (
    <>
      <div className="reg-hero">
        <div className="reg-hero-grid" />
        <div className="reg-hero-ghost">JOIN ADBA</div>
        <div className="reg-hero-left">
          <div className="reg-hero-pill"><span className="reg-hero-dot" />Anand District Badminton Association</div>
          <h1><span className="l1">PLAYER</span><span className="l2">REGISTRATION.</span></h1>
        </div>
        <div className="reg-hero-right">
          <p className="reg-hero-sub">Complete the online registration form to affiliate with Anand District Badminton Association. Unlock access to official state tournaments, ranking systems, and premium coaching plans.</p>
        </div>
      </div>

      <div className="reg-body">
        <div className="reg-container">
          <div className="reg-stepper">
            <div className="reg-stepper-progress" style={{ width: `${((currentStep - 1) / 2) * 100}%` }} />
            {[
              { step: 1, label: "Personal", Icon: UserIcon },
              { step: 2, label: "Affiliation", Icon: MapPinIcon },
              { step: 3, label: "Documents", Icon: FileTextIcon }
            ].map(n => (
              <div key={n.step} className={`reg-step-node ${currentStep === n.step ? "active" : ""} ${currentStep > n.step ? "completed" : ""}`} onClick={() => { if (n.step < currentStep) setCurrentStep(n.step); }}>
                <div className="reg-step-circle">{currentStep > n.step ? "✓" : n.step}</div>
                <span className="reg-step-label">{n.label}</span>
              </div>
            ))}
          </div>

          <form onSubmit={handleFormSubmit} className="reg-form-card">
            {renderStepContent()}
            <div className="reg-actions">
              {currentStep > 1 && (
                <button type="button" onClick={handleBack} className="reg-btn-back" disabled={isSubmitting}><ArrowLeftIcon /> Back</button>
              )}
              {currentStep < 3 ? (
                <button type="button" onClick={handleNext} className="reg-btn-next">Next <ArrowRightIcon /></button>
              ) : (
                <button type="submit" className={`reg-btn-next ${isSubmitting ? "loading" : ""}`} disabled={isSubmitting}>
                  {isSubmitting ? <><div className="reg-shuttle-loader" /> Submitting Record...</> : <>Complete Profile<ArrowRightIcon /></>}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default PlayerRegistration;